import React, { useState, useEffect } from 'react';
import { getRecentAttackers, removeAttacker, reportToSwat, getSwatStatus, getLawyerPricing, hireLawyer } from '../services/api';
import './SwatSystem.css';

const SwatSystem = () => {
  const [recentAttackers, setRecentAttackers] = useState([]);
  const [swatStatus, setSwatStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [removing, setRemoving] = useState(new Set()); // Track which attackers are being removed
  const [hiringLawyer, setHiringLawyer] = useState(false);
  const [lawyerPricing, setLawyerPricing] = useState(null);
  const [reductionHours, setReductionHours] = useState(0);
  const [showLawyerBar, setShowLawyerBar] = useState(false);

  useEffect(() => {
    fetchData();
    // Check status every 30 seconds
    const interval = setInterval(fetchSwatStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [attackersData, statusData] = await Promise.all([
        getRecentAttackers(),
        getSwatStatus()
      ]);
      
      if (attackersData.success) {
        setRecentAttackers(attackersData.data.recentAttackers);
      }
      
      if (statusData.success) {
        setSwatStatus(statusData.data);
        
        // If user is in jail, fetch lawyer pricing
        if (statusData.data.jailStatus.isInJail) {
          try {
            const pricingData = await getLawyerPricing();
            if (pricingData.success) {
              setLawyerPricing(pricingData.data);
              setReductionHours(0); // Reset slider
            }
          } catch (pricingErr) {
            console.error('Error fetching lawyer pricing:', pricingErr);
          }
        } else {
          setLawyerPricing(null);
          setShowLawyerBar(false);
        }
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching SWAT data:', err);
      setError('Failed to load SWAT system data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSwatStatus = async () => {
    try {
      const statusData = await getSwatStatus();
      if (statusData.success) {
        setSwatStatus(statusData.data);
        
        // Show status update if available
        if (statusData.data.statusUpdate) {
          const update = statusData.data.statusUpdate;
          if (update.type === 'jailed') {
            alert(`🚔 ${update.message}`);
          } else if (update.type === 'escaped') {
            alert(`🏃 ${update.message}`);
          } else if (update.type === 'released') {
            alert(`🆓 ${update.message}`);
          }
        }
      }
    } catch (err) {
      console.error('Error checking SWAT status:', err);
    }
  };

  const handleReportToSwat = async (targetUserId, targetUsername) => {
    if (!window.confirm(`Are you sure you want to report ${targetUsername} to SWAT?\n\nThis will send a SWAT team after them. They will have 1 hour to move their computer 500+ miles or face jail time.`)) {
      return;
    }

    try {
      setReporting(true);
      // For scanner attacks (userId is null), use username. For regular attacks, use userId.
      const response = await reportToSwat(targetUserId, targetUserId ? null : targetUsername);
      
      if (response.success) {
        const baseMessage = response.message;
        const finalMessage = response.data.removedFromList 
          ? `${baseMessage}\n\n✅ ${targetUsername} has been automatically removed from your Recent Attackers list.`
          : baseMessage;
        
        alert(`🚔 ${finalMessage}`);
        
        // Refresh data to show updated list
        fetchData();
      } else {
        alert(`❌ Failed to report: ${response.message}`);
      }
    } catch (err) {
      console.error('Error reporting to SWAT:', err);
      alert('❌ Error reporting to SWAT. Please try again.');
    } finally {
      setReporting(false);
    }
  };

  const handleRemoveAttacker = async (attackerId, attackerUsername) => {
    if (!window.confirm(`Are you sure you want to remove ${attackerUsername} from your attackers list?\n\nOnce removed, you will no longer be able to report them to SWAT for this attack.`)) {
      return;
    }

    try {
      setRemoving(prev => new Set(prev).add(attackerId));
      const response = await removeAttacker(attackerId);
      
      if (response.success) {
        alert(`✅ ${attackerUsername} has been removed from your attackers list.`);
        // Refresh data to show updated list
        fetchData();
      } else {
        alert(`❌ Failed to remove attacker: ${response.message}`);
      }
    } catch (err) {
      console.error('Error removing attacker:', err);
      alert('❌ Error removing attacker. Please try again.');
    } finally {
      setRemoving(prev => {
        const newSet = new Set(prev);
        newSet.delete(attackerId);
        return newSet;
      });
    }
  };

  const handleHireLawyer = async () => {
    if (reductionHours <= 0) {
      alert('Please select the amount of time you want to reduce using the slider.');
      return;
    }

    const cost = Math.ceil(reductionHours * 300);
    const taxAmount = Math.floor(cost * 0.20);
    
    if (!window.confirm(`Are you sure you want to hire a lawyer?\n\nReduction: ${reductionHours.toFixed(1)} hours\nCost: ${cost} WR Coins\nTax (20%): ${taxAmount} WR Coins\nTotal Deducted: ${cost} WR Coins`)) {
      return;
    }

    try {
      setHiringLawyer(true);
      const response = await hireLawyer(reductionHours);
      
      if (response.success) {
        alert(`⚖️ ${response.message}\n\nCost: ${response.data.cost} WR Coins\nTax to Occult Bank: ${response.data.taxAmount} WR Coins\nTime Reduced: ${response.data.timeReduced.toFixed(1)} hours\nRemaining Coins: ${response.data.remainingCoins}`);
        
        // Hide lawyer bar after successful purchase
        setShowLawyerBar(false);
        setReductionHours(0);
        
        // Refresh data from database
        fetchData();
      } else {
        alert(`❌ Failed to hire lawyer: ${response.message}`);
      }
    } catch (err) {
      console.error('Error hiring lawyer:', err);
      alert('❌ Error hiring lawyer. Please try again.');
    } finally {
      setHiringLawyer(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const attackTime = new Date(timestamp);
    const diffMinutes = Math.floor((now - attackTime) / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffMinutes < 1440) {
      return `${Math.floor(diffMinutes / 60)} hours ago`;
    } else {
      return `${Math.floor(diffMinutes / 1440)} days ago`;
    }
  };

  const formatTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diffMinutes = Math.ceil((end - now) / (1000 * 60));
    
    if (diffMinutes <= 0) return 'Expired';
    
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  if (loading) {
    return (
      <div className="swat-system">
        <div className="loading">Loading SWAT system...</div>
      </div>
    );
  }

  return (
    <div className="swat-system">
      <div className="swat-header">
        <h2>🚔 SWAT Defense System</h2>
        <p>Report attackers to law enforcement and defend yourself from hackers!</p>
      </div>

      {/* SWAT/Jail Status */}
      {swatStatus && (
        <div className="status-section">
          {swatStatus.swatStatus.isSwatted && (
            <div className="swat-alert alert-warning">
              <h3>⚠️ SWAT TEAM INCOMING!</h3>
              <p>You have been reported to SWAT by <strong>{swatStatus.swatStatus.swattedBy.username}</strong></p>
              <p>⏰ Time remaining: {formatTimeRemaining(swatStatus.swatStatus.swatDeadline)}</p>
              <p>🏃 Move your computer 500+ miles to avoid jail!</p>
            </div>
          )}
          
          {swatStatus.jailStatus.isInJail && (
            <div className="jail-alert alert-danger">
              <h3>🔒 YOU ARE IN JAIL</h3>
              <div className="jail-details">
                <p><strong>👮 Arrested by:</strong> {swatStatus.jailStatus.jailedBy?.username || 'SWAT Team'}</p>
                <p><strong>⏰ Time remaining:</strong> {formatTimeRemaining(swatStatus.jailStatus.jailEndTime)}</p>
                <p><strong>🕐 Jail started:</strong> {new Date(swatStatus.jailStatus.jailStartTime).toLocaleString()}</p>
                {swatStatus.jailStatus.crimeType && (
                  <p><strong>⚖️ Crime:</strong> {swatStatus.jailStatus.crimeType.replace('_', ' ')}</p>
                )}
                {swatStatus.jailStatus.originalSentence && (
                  <p><strong>📝 Original sentence:</strong> {swatStatus.jailStatus.originalSentence} hours</p>
                )}
              </div>
              
              <div className="jail-effects">
                <h4>🚫 While in jail you cannot:</h4>
                <ul>
                  <li>🖥️ Use your computer (hidden from other players)</li>
                  <li>🔓 Perform brute force attacks</li>
                  <li>🌐 Launch network injections</li>
                  <li>💰 Access banks or casinos</li>
                  <li>🏃 Move your computer location</li>
                  <li>🎯 Report other users to SWAT</li>
                </ul>
              </div>
              
              <div className="lawyer-section">
                {!showLawyerBar && (
                  <button 
                    className="lawyer-button show-lawyer-bar"
                    onClick={() => setShowLawyerBar(true)}
                    disabled={hiringLawyer || !lawyerPricing}
                  >
                    ⚖️ Hire Lawyer (300 WR Coins per hour)
                  </button>
                )}
                
                {showLawyerBar && lawyerPricing && (
                  <div className="lawyer-bar-container">
                    <div className="lawyer-bar-header">
                      <h4>⚖️ Lawyer Services</h4>
                      <button 
                        className="close-lawyer-bar"
                        onClick={() => {setShowLawyerBar(false); setReductionHours(0);}}
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="lawyer-pricing-info">
                      <p>💰 Your WR Coins: <strong>{lawyerPricing.currentCoins}</strong></p>
                      <p>⏰ Time Remaining: <strong>{lawyerPricing.hoursRemaining.toFixed(1)} hours</strong></p>
                      <p>💸 Rate: <strong>300 WR Coins per hour</strong></p>
                      <p>📊 Tax: <strong>20% goes to Occult Bank</strong></p>
                    </div>
                    
                    <div className="lawyer-slider-container">
                      <label htmlFor="reduction-slider">Time Reduction: {reductionHours.toFixed(1)} hours</label>
                      <input
                        id="reduction-slider"
                        type="range"
                        min="0"
                        max={lawyerPricing.maxReductionHours}
                        step="0.1"
                        value={reductionHours}
                        onChange={(e) => setReductionHours(parseFloat(e.target.value))}
                        className="lawyer-slider"
                      />
                      <div className="slider-labels">
                        <span>0 hrs</span>
                        <span>{lawyerPricing.maxReductionHours.toFixed(1)} hrs (max)</span>
                      </div>
                    </div>
                    
                    <div className="lawyer-cost-display">
                      <div className="cost-breakdown">
                        <p>💰 Cost: <strong>{Math.ceil(reductionHours * 300)} WR Coins</strong></p>
                        <p>📊 Tax (20%): <strong>{Math.floor(Math.ceil(reductionHours * 300) * 0.20)} WR Coins</strong></p>
                        <p>⏰ New Release Time: <strong>
                          {reductionHours > 0 ? 
                            new Date(new Date(lawyerPricing.jailEndTime).getTime() - (reductionHours * 60 * 60 * 1000)).toLocaleString() :
                            new Date(lawyerPricing.jailEndTime).toLocaleString()
                          }
                        </strong></p>
                      </div>
                    </div>
                    
                    <div className="lawyer-actions">
                      <button 
                        className="hire-lawyer-confirm"
                        onClick={handleHireLawyer}
                        disabled={hiringLawyer || reductionHours <= 0 || Math.ceil(reductionHours * 300) > lawyerPricing.currentCoins}
                      >
                        {hiringLawyer ? 'Hiring Lawyer...' : `⚖️ Hire Lawyer (${Math.ceil(reductionHours * 300)} WR Coins)`}
                      </button>
                    </div>
                  </div>
                )}
                
                {swatStatus.jailStatus.lawyerHired && swatStatus.jailStatus.timeReduced && (
                  <div className="lawyer-hired">
                    <p>⚖️ Lawyer previously hired!</p>
                    <p>⏰ Total time reduced: {swatStatus.jailStatus.timeReduced.toFixed(1)} hours</p>
                    <p>💰 Total cost: {swatStatus.jailStatus.totalLawyerCost || swatStatus.jailStatus.lawyerCost} WR Coins</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Recent Attackers */}
      <div className="attackers-section">
        <h3>🎯 Recent Attackers (Last 24 Hours)</h3>
        <p style={{ fontSize: '0.9rem', color: '#888', marginBottom: '15px' }}>
          💡 You can report attackers to SWAT or remove them from your list. Attackers are automatically removed after 24 hours.
        </p>
        
        {recentAttackers.length === 0 ? (
          <div className="no-attackers">
            <div className="no-attackers-content">
              <h4>🛡️ No Recent Attacks</h4>
              <p>You haven't been attacked in the last 24 hours.</p>
              <p>If someone attacks you, they'll appear here and you can report them to SWAT!</p>
            </div>
          </div>
        ) : (
          <div className="attackers-grid">
            {recentAttackers.map((attacker, index) => (
              <div key={`${attacker.userId}-${index}`} className="attacker-card">
                <div className="attacker-header">
                  <h4>👤 {attacker.username}</h4>
                  <div className="attack-type">
                    {attacker.attackType === 'network_injection' ? '🌐 Network Injection' :
                     attacker.attackType === 'brute_force' ? '🔓 Brute Force' :
                     attacker.attackType === 'Network Discovery Scan' ? '🔍 Network Scan' :
                     attacker.attackType === 'Bank Data Extraction' ? '📊 Data Extraction' :
                     attacker.attackType === 'Bank Account Breach' ? '💰 Scanner Breach' :
                     '⚡ ' + attacker.attackType}
                  </div>
                </div>
                
                <div className="attack-details">
                  <div className="detail-row">
                    <span className="label">Attacked:</span>
                    <span className="value">{formatTimeAgo(attacker.attackedAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Attack Type:</span>
                    <span className="value">{attacker.attackType.replace('_', ' ')}</span>
                  </div>
                </div>
                
                <div className="attacker-actions">
                  <button 
                    className="report-button"
                    onClick={() => handleReportToSwat(attacker.userId, attacker.username)}
                    disabled={reporting || (swatStatus && swatStatus.jailStatus.isInJail)}
                  >
                    {reporting ? 'Reporting...' : '🚔 Report to SWAT'}
                  </button>
                  
                  <button 
                    className="remove-button"
                    onClick={() => handleRemoveAttacker(attacker._id, attacker.username)}
                    disabled={removing.has(attacker._id)}
                    title="Remove this attacker from your list"
                  >
                    {removing.has(attacker._id) ? 'Removing...' : '🗑️ Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="swat-info">
        <h3>ℹ️ How SWAT System Works</h3>
        <div className="info-content">
          <div className="info-item">
            <span className="info-icon">🎯</span>
            <div className="info-text">
              <strong>Automatic SWAT:</strong> Every attack has a chance to trigger SWAT: Network injection (60%), ATMs/Chests (10%), Casino Slots (70%), Casino vaults (22%), Banks (70%), Scanner brute force (70%)
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🚔</span>
            <div className="info-text">
              <strong>Manual Reports:</strong> You can also report attackers manually - they get 1 hour to move 500+ miles away
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🔒</span>
            <div className="info-text">
              <strong>Jail Time:</strong> Minor crimes (network injection, slot machines, ATMs): 5-20 hours. Major crimes (banks, casinos, treasure chests): 20 hours to 10 days
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">⚖️</span>
            <div className="info-text">
              <strong>Lawyer:</strong> Hire a lawyer while in jail to reduce your sentence by 50% (costs 10 WR Coins per hour remaining)
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🔍</span>
            <div className="info-text">
              <strong>Scanner Detection:</strong> Users are automatically notified when someone scans their network, extracts their bank data, or attempts brute force attacks on their accounts
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🗑️</span>
            <div className="info-text">
              <strong>Remove Attackers:</strong> You can manually remove attackers from your list if you don't want to report them to SWAT
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">⏰</span>
            <div className="info-text">
              <strong>Auto-Cleanup:</strong> Attackers are automatically removed from your list after 24 hours
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🏃</span>
            <div className="info-text">
              <strong>Escape:</strong> Smart hackers can evade SWAT by quickly relocating their operations
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwatSystem;